// SignInForm.tsx
import { Form, Input, Checkbox } from "antd";
import { AppButton } from "components/button/AppButton";
import { Link, useNavigate } from "react-router-dom";
import {
  emailValidationRules,
  generalValidationRules,
} from "utils/formHelpers/validations";
import { loginUser } from "../hooks/useLoginUser";
import { useMutation } from "react-query";
import { useSignIn } from "react-auth-kit";
import { ISignInUserProps } from "../types";
import { openNotification } from "utils/notification";
import googleLogo from "assets/images/googleIcon.png";
import { useState, useEffect, useRef } from "react";
import { END_POINT } from "config/environment";
import { BeatLoader } from "react-spinners";
// import { Turnstile } from "components/Turnstile"; 
import { useTurnstileEnabled } from "../../../hooks/useFeatureFlag";

interface SignInFormProps {
  defaultAccountType?: string | null;
}

export const SignInForm = ({ defaultAccountType }: SignInFormProps) => {
  const navigate = useNavigate();
  const signIn = useSignIn();
  const turnstileErrorCount = useRef(0);

  const { mutate, isLoading } = useMutation(loginUser);
  const [isNotify, setNotification] = useState(false);
  const [type, setType] = useState(defaultAccountType || "");
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [isTurnstileValid, setIsTurnstileValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isTurnstileEnabled = useTurnstileEnabled();
  const TURNSTILE_SITE_KEY =
    process.env.NODE_ENV === "development"
      ? "1x00000000000000000000AA"
      : process.env.REACT_APP_TURNSTILE_SITE_KEY || "";
  const MAX_LOGIN_ATTEMPTS = 3;
  const [captchaRetryCount, setCaptchaRetryCount] = useState(0);
  const MAX_CAPTCHA_RETRIES = 3;

  // Load Turnstile script on mount - Commented out for now
  // useEffect(() => {
  //   if (isTurnstileEnabled) {
  //     if (!window.turnstile) {
  //       const script = document.createElement("script");
  //       script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
  //       script.async = true;
  //       script.defer = true;
  //       script.onload = () => {
  //         console.log("Turnstile loaded");
  //       };
  //       script.onerror = () => {
  //         console.error("Failed to load Turnstile");
  //       };
  //       document.head.appendChild(script);
  //     }
  //   }
  // }, [isTurnstileEnabled]);

  useEffect(() => {
    if (defaultAccountType) {
      setType(defaultAccountType);
      form.setFieldsValue({ type: defaultAccountType });
    }
  }, [defaultAccountType, form]);

  const handleFailedLogin = () => {
    const newAttempts = loginAttempts + 1;
    setLoginAttempts(newAttempts);

    console.log(`Login attempt ${newAttempts} failed`);

    // CAPTCHA logic - Commented out for now
    // if (
    //   isTurnstileEnabled &&
    //   newAttempts >= MAX_LOGIN_ATTEMPTS &&
    //   !showCaptcha
    // ) {
    //   console.log("Showing captcha after 3 failures");
    //   setShowCaptcha(true);
    //   setTurnstileToken(null);
    //   setIsTurnstileValid(false);
    //
    //   openNotification({
    //     title: "Verification Required",
    //     state: "info",
    //     description:
    //       "Multiple failed login attempts. Please complete the CAPTCHA verification to continue.",
    //     duration: 4.5,
    //   });
    // }
  };

  // CAPTCHA handlers - Commented out for now
  // const handleTurnstileVerify = (token: string) => {
  //   console.log("Turnstile verified successfully");
  //   setTurnstileToken(token);
  //   setIsTurnstileValid(true);
  // };
  //
  // const handleTurnstileError = (error: string) => {
  //   console.error("Turnstile error:", error);
  //
  //   openNotification({
  //     title: "Verification Error",
  //     state: "error",
  //     description: "CAPTCHA verification failed. Please try again.",
  //     duration: 4.5,
  //   });
  // };
  //
  // const handleTurnstileExpire = () => {
  //   console.log("Turnstile expired");
  //   setTurnstileToken(null);
  //   setIsTurnstileValid(false);
  //
  //   openNotification({
  //     title: "Verification Expired",
  //     state: "info",
  //     description: "CAPTCHA expired. Please complete it again.",
  //     duration: 4.5,
  //   });
  // };

  const handleSubmit = (data: any) => {
    // CAPTCHA validation - Commented out for now
    // if (isTurnstileEnabled && showCaptcha) {
    //   if (!turnstileToken) {
    //     openNotification({
    //       title: "Verification Required",
    //       state: "error",
    //       description: "Please complete the CAPTCHA verification.",
    //       duration: 4.5,
    //     });
    //     return;
    //   }
    //   if (!isTurnstileValid) {
    //     openNotification({
    //       title: "Verification Failed",
    //       state: "error",
    //       description: "CAPTCHA verification invalid. Please try again.",
    //       duration: 4.5,
    //     });
    //     return;
    //   }
    // }

    setIsSubmitting(true);

    const props: ISignInUserProps = {
      email: data.email,
      password: data.password,
    };

    if (type) {
      props.type = type;
    }

    // CAPTCHA token - Commented out for now
    // if (isTurnstileEnabled && showCaptcha && turnstileToken) {
    //   props.turnstile_token = turnstileToken;
    //   console.log("Sending turnstile token with login request");
    // }

    setNotification(true);

    mutate(props, {
      onError: (err: any) => {
        console.error("Login error:", err);
        setNotification(false);
        setIsSubmitting(false);

        // CAPTCHA error handling - Commented out for now
        // if (
        //   err?.response?.status === 400 &&
        //   err?.response?.data?.err === 110200
        // ) {
        //   // Increment CAPTCHA retry count
        //   const newRetryCount = captchaRetryCount + 1;
        //   setCaptchaRetryCount(newRetryCount);
        //
        //   // Check if max retries reached
        //   if (newRetryCount >= MAX_CAPTCHA_RETRIES) {
        //     // Reset everything and start over
        //     setShowCaptcha(false);
        //     setLoginAttempts(0);
        //     setCaptchaRetryCount(0);
        //
        //     openNotification({
        //       title: "Too Many Attempts",
        //       state: "error",
        //       description:
        //         "Too many failed CAPTCHA attempts. Please try again later.",
        //       duration: 8.0,
        //     });
        //
        //     // Optionally redirect or reload
        //     return;
        //   }
        //
        //   // Count this as a failed attempt
        //   handleFailedLogin();
        //
        //   // Reset turnstile token for retry
        //   setTurnstileToken(null);
        //   setIsTurnstileValid(false);
        //   setTurnstileKey((prev) => prev + 1);
        //
        //   openNotification({
        //     title: `CAPTCHA Verification Failed (${newRetryCount}/${MAX_CAPTCHA_RETRIES})`,
        //     state: "error",
        //     description:
        //       err?.response?.data?.message ||
        //       "The CAPTCHA verification failed. Please try again.",
        //     duration: 8.0,
        //   });
        //   return;
        // }

        const errorMsg =
          err?.response?.data?.msg ||
          err?.response?.data?.message ||
          "Invalid credentials";

        openNotification({
          title: "Error",
          state: "error",
          description: errorMsg,
          duration: 8.0,
        });

        // Handle failed login attempt (non-CAPTCHA errors)
        handleFailedLogin();
      },
      onSuccess: (res: any) => {
        const result = res.data;
        const expiresIn = data.remember ? 60 * 24 * 7 : 60;

        localStorage.setItem("lastAccountType", type);

        if (
          signIn({
            token: result.token,
            tokenType: "Bearer",
            authState: result,
            expiresIn,
          })
        ) {
          openNotification({
            title: "Success",
            state: "success",
            description: "Logged in successfully",
            duration: 4.5,
          });

          setShowCaptcha(false);
          setTurnstileToken(null);
          setIsTurnstileValid(false);
          setLoginAttempts(0);
          turnstileErrorCount.current = 0;

          // Reset all states
          setShowCaptcha(false);
          setTurnstileToken(null);
          setIsTurnstileValid(false);
          setLoginAttempts(0);
          navigate(`/dashboard/${result?.user?.id}`);
        }

        setNotification(false);
        setIsSubmitting(false);
      },
    });
  };

  const redirect = (platform: string) => {
    if (!type) {
      openNotification({
        title: "Error",
        state: "error",
        description: "Please select an account type",
        duration: 4.5,
      });
      return;
    }
    setLoading(true);
    fetch(
      `${END_POINT.AUTH_URL}/${
        platform === "google" ? "auth" : "auth/linkedin"
      }?type=${type}&is_corporate=2`,
      {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      },
    )
      .then((response) => {
        if (response.ok) {
          return response.json();
        }
        setLoading(false);
        throw new Error("Something went wrong!");
      })
      .then((data) => window.location.assign(data.url))
      .catch((error) => console.error(error));
  };

  return (
    <>
      <Form
        form={form}
        onFinish={handleSubmit}
        layout="vertical"
        requiredMark={false}
      >
        <Form.Item name="email" label="Email" rules={emailValidationRules}>
          <Input placeholder="Enter your email" className="py-2 shadow-sm" />
        </Form.Item>

        <Form.Item
          name="password"
          label="Password"
          rules={generalValidationRules}
          className="-mt-3"
        >
          <Input.Password
            placeholder="Enter your password"
            className="shadow-sm py-2"
          />
        </Form.Item>

        <div className="flex justify-between mb-6 -mt-2">
          <Form.Item
            name="remember"
            valuePropName="checked"
            initialValue={false}
          >
            <Checkbox>Remember me</Checkbox>
          </Form.Item>

          <Link
            to={`/forgot-password/${type || defaultAccountType || "talent"}`}
            className="text-primary font-medium"
          >
            Forgot password
          </Link>
        </div>

        {/* CAPTCHA - Commented out for now */}
        {/* {isTurnstileEnabled && showCaptcha && (
          <Form.Item
            label="Verification"
            required
            validateStatus={
              isTurnstileValid
                ? "success"
                : turnstileToken !== null
                  ? "error"
                  : ""
            }
            help={
              turnstileToken !== null && !isTurnstileValid
                ? "CAPTCHA verification failed. Please try again."
                : "Please complete the CAPTCHA verification to continue."
            }
          >
            <Turnstile
              key={turnstileKey}
              sitekey={TURNSTILE_SITE_KEY}
              onVerify={handleTurnstileVerify}
              onError={handleTurnstileError}
              onExpire={handleTurnstileExpire}
              theme="auto"
              size="normal"
            />
          </Form.Item>
        )} */}

        <AppButton
          label="Sign in"
          type="submit"
          additionalClassNames={[
            "w-full",
            "bg-primary",
            "text-white",
            "py-3.5",
            "rounded-xl",
            "font-medium",
            "mb-4",
            "mt-10",
          ]}
          isLoading={isNotify || isSubmitting}
          disabled={isNotify || isSubmitting}
          // CAPTCHA disabled logic - Commented out for now
          // disabled={
          //   isNotify ||
          //   isSubmitting ||
          //   (isTurnstileEnabled && showCaptcha && !isTurnstileValid)
          // }
        />
      </Form>

      <button
        onClick={() => redirect("google")}
        className="w-full border border-gray-300 py-3 rounded-xl flex items-center justify-center gap-3 hover:bg-gray-50"
      >
        <img src={googleLogo} alt="google auth" className="h-5" />
        <span>
          {loading ? <BeatLoader color="#222" /> : "Sign in with Google"}
        </span>
      </button>
    </>
  );
};
